import { CHANNELS } from "../shared/channels";
import { UserSettings } from "../shared/types";
import { call } from "./bridge";
import { cachedSettings, rememberSettings } from "./settings_cache";

// the notes this person pinned to their home, by note name, saved per person as pinnedNotes in their settings.
// pinned notes lead the home notes module in the order they were pinned

export const MAX_NOTE_PINS = 8;

let pinned: string[] = [];
let loaded: Promise<void> | null = null;
let saveTimer = 0;
const watchers: Array<() => void> = [];

function tidy(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const seen = new Set<string>();
  for (const name of list) if (typeof name === "string" && name && seen.size < MAX_NOTE_PINS) seen.add(name);
  return [...seen];
}

export function onNotePinsChanged(watcher: () => void): void {
  watchers.push(watcher);
}

function changed(): void {
  watchers.forEach((watcher) => watcher());
}

function save(): void {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    void call<UserSettings>(CHANNELS.settingsSave, { pinnedNotes: pinned }).then(rememberSettings).catch(() => undefined);
  }, 350);
}

// the pins are read from settings once, then kept here
export function loadNotePins(): Promise<void> {
  loaded ??= cachedSettings()
    .then((settings) => {
      pinned = tidy(settings.pinnedNotes);
    })
    .catch(() => {
      loaded = null;
    });
  return loaded;
}

export function notePins(): string[] {
  return pinned;
}

export function isNotePinned(name: string): boolean {
  return pinned.includes(name);
}

// false when all the pins are taken, the caller says so
export function toggleNotePin(name: string): boolean {
  if (pinned.includes(name)) pinned = pinned.filter((item) => item !== name);
  else if (pinned.length >= MAX_NOTE_PINS) return false;
  else pinned = [...pinned, name];
  changed();
  save();
  return true;
}

// a renamed note keeps its place among the pins
export function renameNotePin(from: string, to: string): void {
  if (from === to || !pinned.includes(from)) return;
  pinned = tidy(pinned.map((name) => (name === from ? to : name)));
  changed();
  save();
}

// pins for notes that no longer exist go, so they stop using up the slots
export function pruneNotePins(existing: string[]): void {
  const have = new Set(existing);
  const kept = pinned.filter((name) => have.has(name));
  if (kept.length === pinned.length) return;
  pinned = kept;
  changed();
  save();
}

export function dropNotePin(name: string): void {
  if (!pinned.includes(name)) return;
  pinned = pinned.filter((item) => item !== name);
  changed();
  save();
}

// the notes to show on home: pinned first in pin order, then the newest to fill the room
export function homeNotes<T extends { name: string }>(notes: T[], room: number): T[] {
  const first = pinned.map((name) => notes.find((note) => note.name === name)).filter((note): note is T => Boolean(note));
  const rest = notes.filter((note) => !pinned.includes(note.name));
  return [...first, ...rest].slice(0, Math.max(room, first.length));
}
